import React, { useEffect, useState } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ApolloProvider } from '@apollo/react-hooks';
import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { setContext } from 'apollo-link-context';
import { createUploadLink } from 'apollo-upload-client';
import { onError } from 'apollo-link-error';
import { persistCache } from 'apollo-cache-persist';
import { InMemoryCache, NormalizedCacheObject } from 'apollo-cache-inmemory';
import { useAlert } from 'react-alert';

import useIsComDomain from '@src/hooks/useIsComDomain';
import { CredentialProvider, ServiceWorkerProvider, MenuDataProvider } from '@providers';

import { Loading } from '@atoms';
import App from '@src/app/App';

import { IGNORE_GRAPHQL_ERROR_MESSAGES, NEED_SESSION_ERROR_MESSAGE } from '@src/utils/constants';
import { accountLoginUrl } from '@src/app/routes';

const ApolloApp: React.FC = ({}) => {
  const alert = useAlert();
  const isComDomain = useIsComDomain();
  const api_uri = isComDomain ? process.env.COM_API_URI : process.env.API_URI;

  const linkOptions = {
    uri: api_uri,
  };

  //TODO: Consider to upgrade apollo from 2.6 to 3
  const defaultOptions = {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  };

  const authLink = setContext((_, { headers }) => {
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    const token = currentUser?.token;
    return {
      headers: {
        ...headers,
        ...(token && { authorization: `JWT ${token}` }),
      },
    };
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      let errMsgs = graphQLErrors.map((err) => err.message);

      if (errMsgs.includes(NEED_SESSION_ERROR_MESSAGE)) {
        localStorage.removeItem('currentUser');
        window.location.href = accountLoginUrl;
      }

      errMsgs = errMsgs.filter((msg) => !IGNORE_GRAPHQL_ERROR_MESSAGES.includes(msg));

      if (errMsgs.length) {
        const msg = errMsgs.join('\n');
        alert.error(`[error]\n${msg}`);
      }
    }

    if (networkError) console.log(`[Network error]: ${networkError}`);
  });

  // link is middleware
  // https://www.apollographql.com/docs/react/networking/network-layer/
  const link = ApolloLink.from([
    errorLink,
    authLink,
    // uploadAndBatchHTTPLink(linkOptions),
    //@ts-ignore
    createUploadLink(linkOptions),
  ]);

  const [client, setClient] = useState(undefined);
  useEffect(() => {
    const cache = new InMemoryCache();

    const client: ApolloClient<NormalizedCacheObject> = new ApolloClient({
      cache,
      link,
      //@ts-ignore
      defaultOptions,
    });

    // See above for additional options, including other storage providers.
    persistCache({
      cache,
      storage: window.localStorage,
    }).then(() => {
      client.onResetStore(async () => cache.writeData({ data: {} }));
      setClient(client);
    });
    return () => {};
  }, []);
  if (client === undefined) return <Loading />;

  const serviceWorkerTimeout = 60 * 1000;

  return (
    <ApolloProvider client={client}>
      <ServiceWorkerProvider timeout={serviceWorkerTimeout}>
        <CredentialProvider>
          <GoogleOAuthProvider clientId="5763672168-vj3djj1oqd963jk7vkihet652lvnsqlk.apps.googleusercontent.com">
            <MenuDataProvider>
              <App />
            </MenuDataProvider>
          </GoogleOAuthProvider>
        </CredentialProvider>
      </ServiceWorkerProvider>
    </ApolloProvider>
  );
};

export default ApolloApp;
